import type { ColorProps } from "@chakra-ui/react";
import { t } from "@lingui/macro";
import type { TimeTrackingPopoverItemsQuery } from "@src/__generated__/graphql";
import type { TimeTrackingEntryFragment } from "@src/__generated__/urql-graphql";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import type { Time } from "@src/utils/types";
import { groupBy, keys } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { createRef } from "react";
import { EntryForm } from "../components/widgets/TimeTrackingPopover/form";

export class TimeTrackingPopoverStore implements BaseStore {
  appStore: AppStore;
  @observable.ref popoverActive = false;
  @observable.ref popoverRef = createRef<HTMLDivElement>();

  form: EntryForm = new EntryForm();

  @observable.ref groupedEntries: [string, TimeTrackingEntryFragment[]][] = [];
  refetchEntries: () => void = () => {};

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
  }

  @computed get bg(): ColorProps["color"] {
    return this.appStore.timeTrackingStore.runningHours >= 24
      ? "red.100"
      : this.appStore.timeTrackingStore.runningHours >= 12
        ? "orange.100"
        : "violet.100";
  }

  @computed get iconBg(): ColorProps["color"] {
    return this.appStore.timeTrackingStore.runningHours >= 24
      ? "red.200"
      : this.appStore.timeTrackingStore.runningHours >= 12
        ? "orange.200"
        : "violet.200";
  }

  @computed get activatorColor(): ColorProps["color"] {
    return this.appStore.timeTrackingStore.runningHours >= 24
      ? "red.800"
      : this.appStore.timeTrackingStore.runningHours >= 12
        ? "orange.800"
        : this.appStore.timeTrackingStore.runningEntry
          ? "violet.700"
          : "violet.600";
  }

  @computed get longRunningText(): string {
    return this.appStore.timeTrackingStore.runningHours >= 24
      ? t`Check your time because timer ran through the night.`
      : this.appStore.timeTrackingStore.runningHours >= 12
        ? t`Are you sure that this time entry is correct? It is more than 12 hours.`
        : "";
  }

  @action.bound
  togglePopover(value?: boolean) {
    this.popoverActive = value ?? !this.popoverActive;
  }

  @action
  setEntries(src: TimeTrackingPopoverItemsQuery) {
    const timeTrackingItems = src.timeTrackingItems.map((i) => ({
      ...i,
      specific_time_from: i.specific_time_from
        ? (i.specific_time_from as Time)
        : undefined,
      specific_time_to: i.specific_time_to
        ? (i.specific_time_to as Time)
        : undefined,
      tracked_for_date: new Date(i.tracked_for_date),
    }));

    const groups = groupBy(timeTrackingItems, "tracked_for_date");
    this.groupedEntries = keys(groups)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map((i) => [i, groups[i]]);

    this.appStore.timeTrackingStore.runningEntry = src.runningTimer
      ?.timeTrackingItem
      ? {
          ...src.runningTimer.timeTrackingItem,
          tracked_for_date: new Date(
            src.runningTimer.timeTrackingItem.tracked_for_date,
          ),
          specific_time_from: src.runningTimer.timeTrackingItem
            .specific_time_from
            ? (src.runningTimer.timeTrackingItem.specific_time_from as Time)
            : undefined,
          specific_time_to: src.runningTimer.timeTrackingItem.specific_time_to
            ? (src.runningTimer.timeTrackingItem.specific_time_to as Time)
            : undefined,
        }
      : undefined;
  }
}
